const SERVICE_WORKER_FILE = 'sw.js';

function buildServiceWorkerUrl(fileName: string): string {
  const base = import.meta.env.BASE_URL ?? '/';
  return new URL(fileName, `${window.location.origin}${base}`).toString();
}

function getRegistrationErrorMessage(error: unknown): string {
  if (error instanceof DOMException) {
    return `${error.name}: ${error.message}`;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Unknown error';
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (import.meta.env.DEV) {
    return null;
  }

  if (!('serviceWorker' in navigator)) {
    console.info('Service workers are not supported in this browser.');
    return null;
  }

  if (!window.isSecureContext) {
    console.warn(
      'Service workers can only be registered in a secure context (HTTPS or localhost).',
    );
    return null;
  }

  const serviceWorkerUrl = buildServiceWorkerUrl(SERVICE_WORKER_FILE);
  const scope = import.meta.env.BASE_URL ?? '/';
  const serviceWorkerContainer =
    navigator.serviceWorker as ServiceWorkerContainer;

  try {
    const response = await fetch(serviceWorkerUrl, {
      method: 'HEAD',
      credentials: 'same-origin',
    });

    if (!response.ok) {
      console.error(
        `Service worker file check failed: ${serviceWorkerUrl} returned ${response.status} ${response.statusText}.`,
      );
      return null;
    }
  } catch (error) {
    console.error('Unable to reach the service worker file.', error);
    return null;
  }

  try {
    const registration = await serviceWorkerContainer.register(
      serviceWorkerUrl,
      { scope },
    );
    console.info(
      `Service worker registered with scope "${registration.scope}".`,
    );
    return registration;
  } catch (error) {
    const message = getRegistrationErrorMessage(error);
    console.error(`Service worker registration failed: ${message}.`, error);
    return null;
  }
}
